import {HeaderBackButtonProps} from '@react-navigation/elements';
import {useNavigation} from '@react-navigation/native';
import {ProfileVersion, TemperatureUnit} from 'puffco-api-axios-client';
import React from 'react';
import {Dimensions, Pressable, ScrollView, Text, View} from 'react-native';
import {useSelector} from 'react-redux';
import {ulid} from 'ulid';

import {AppText} from '../../components/AppText';
import {AutoAdjustScreen} from '../../components/AutoAdjustScreen';
import {BackButton} from '../../components/BackButton';
import {Button} from '../../components/Button';
import {MoodLightCircle} from '../../components/MoodLightCircle';
import {
  TextSwitch,
  TextSwitchHandlePosition,
} from '../../components/TextSwitch';
import {Constants} from '../../constants';
import {Messages, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {useSpinner} from '../../contexts/spinner';
import {useRealtimeUnitLighting} from '../../contexts/unit-lighting/useRealtimeUnitLight';
import {CheckIcon, ChevronRightIcon} from '../../icons';
import {useChamberType} from '../../lib/hooks/useChamberType';
import {useDisconnectGuard} from '../../lib/hooks/useDisconnectGuard';
import {useMoodLight} from '../../lib/hooks/useMoodLight';
import {useSafeArea} from '../../lib/hooks/useSafeArea';
import {useTheme} from '../../lib/hooks/useTheme';
import {useThrottle} from '../../lib/hooks/useThrottle';
import {useUpsertHeatProfiles} from '../../lib/hooks/useUpsertHeatProfiles';
import {appSettingsSelector} from '../../lib/redux/slices/appSettingsSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {allHeatProfilesMapSelector} from '../../lib/redux/slices/profilesSlice';
import {userSelector} from '../../lib/redux/slices/userSlice';
import {HeatProfile, Vapor, isColorLight, isMoodLightId} from '../../lib/types';
import {
  changeHexColorValues,
  convertHexToHsl,
  convertHslToHex,
} from '../../lib/utils/colors';
import {useTemperature} from '../../lib/utils/convertTemperature';
import styled from '../../lib/utils/styled';
import type {HeatProfileEditNavigator} from '../../navigators/RootStackNavigator';
import {toSelectMoodLight} from '../../navigators/util';
import {colors} from '../../styles';
import {ColorSlider} from './ColorSlider';
import {DurationSlider} from './DurationSlider';
import {
  HeatProfileNameTextInput,
  isValid as isHeatProfileNameValid,
} from './HeatProfileNameTextInput';
import {TemperatureSlider} from './TemperatureSlider';
import {VaporControlSlider} from './VaporControlSlider';
import {VaporControlUpsellText} from './VaporControlUpsellText';

type Values = Required<
  Pick<
    HeatProfile,
    'name' | 'duration' | 'temperature' | 'vaporSetting' | 'light'
  >
>;

export interface Props {
  onSave: () => void;
  profileId?: string;
  selectedMoodLightId?: string;
}

type Navigation = HeatProfileEditNavigator<
  typeof Screens.HeatProfileEdit | typeof Screens.HeatProfileCreate
>['navigation'];

export const HeatProfileForm = ({
  profileId,
  selectedMoodLightId,
  onSave,
}: Props) => {
  useDisconnectGuard();

  const theme = useTheme();
  const navigation = useNavigation<Navigation>();
  const {top} = useSafeArea();
  const profiles = useSelector(allHeatProfilesMapSelector);
  const user = useSelector(userSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);
  const appSettings = useSelector(appSettingsSelector);

  const {accessMoodLight, canUseMoodLighting, hasSeenEpilepsyWarning} =
    useMoodLight();

  const profile = React.useMemo(() => {
    if (!profileId) return;

    return profiles[profileId];
  }, [profiles, profileId]);

  const initialValues = React.useMemo(() => profile, [profile]);

  const minTemp = useTemperature(
    Constants.TEMPERATURE_MIN_FAHRENHEIT,
    TemperatureUnit.Fahrenheit,
  );

  const maxTemp = useTemperature(
    Constants.TEMPERATURE_MAX_FAHRENHEIT,
    TemperatureUnit.Fahrenheit,
  );

  const initialTemperature = useTemperature(
    initialValues?.temperature ?? Constants.TEMPERATURE_MIN_FAHRENHEIT,
    initialValues?.units ?? TemperatureUnit.Fahrenheit,
  );

  const [state, setState] = React.useState<Values>({
    temperature: initialTemperature,
    duration: initialValues?.duration ?? 40,
    name: initialValues?.name ?? 'NEW PROFILE',
    vaporSetting: initialValues?.vaporSetting ?? 0,
    light: initialValues?.light ?? {color: colors.defaultColor},
  });

  const [textSwitchPosition, setTextSwitchPosition] = React.useState(
    isColorLight(state.light)
      ? TextSwitchHandlePosition.LEFT
      : TextSwitchHandlePosition.RIGHT,
  );

  React.useEffect(() => {
    if (!selectedMoodLightId) return;

    setState(previous => ({
      ...previous,
      light: {id: selectedMoodLightId},
    }));
    setTextSwitchPosition(TextSwitchHandlePosition.RIGHT);
  }, [selectedMoodLightId]);

  const {width} = Dimensions.get('screen');
  const {height: winHeight} = Dimensions.get('window');
  const isSmall =
    (Constants.IS_WEB ? window.innerHeight : winHeight) <=
    Constants.SCREEN_HEIGHT.GALAXY_S8;
  const nameMaxWidth = width * 0.8;

  const canToggleMoodLights =
    canUseMoodLighting && !!user && hasSeenEpilepsyWarning;

  const moodLight = isMoodLightId(state.light)
    ? moodLights[state.light.id]
    : undefined;

  const initialSwitchValue = React.useMemo(() => {
    return isMoodLightId(state.light)
      ? TextSwitchHandlePosition.RIGHT
      : TextSwitchHandlePosition.LEFT;
  }, [state.light]);

  const inputWidth = Math.min(
    state?.name ? (state?.name?.length + 1) * 18 : 0,
    nameMaxWidth,
  );

  const {isVaporEnabled, isMeetAC, is3dChamber, isXLChamber} = useChamberType();

  const vapors = isXLChamber
    ? [Vapor.Standard, Vapor.High, Vapor.Max, Vapor.Xl]
    : [Vapor.Standard, Vapor.High, Vapor.Max];

  const titleStyle = React.useMemo(
    () => ({
      ...(theme.heatProfileEditScreenTheme.navMenuTitleStyle ??
        theme.navMenuTitleStyle),
      color: colors.white,
    }),
    [
      theme.heatProfileEditScreenTheme.navMenuTitleStyle,
      theme.navMenuTitleStyle,
    ],
  );

  const setColor = React.useCallback(
    (color: number) => {
      setState(previous => ({
        ...previous,
        light: {
          color: convertHslToHex(
            color,
            Constants.STD_HSL_VALUES.saturation,
            Constants.STD_HSL_VALUES.lightness,
            true,
          ),
        },
      }));
    },
    [setState],
  );

  const throttledSetColor = useThrottle(setColor, 1000);

  const displayColors = isMoodLightId(state.light)
    ? moodLights[state.light.id]?.projector.meta.userColors ?? []
    : [
        changeHexColorValues(
          state.light.color,
          Constants.STD_HSL_VALUES.saturation,
          Constants.STD_HSL_VALUES.lightness,
          true,
        ),
      ];

  useRealtimeUnitLighting(
    initialValues ? Screens.HeatProfileEdit : Screens.HeatProfileCreate,
    isColorLight(state.light) ? state.light : moodLights[state.light.id],
  );

  const [{loading}, upsertAccountHeatProfiles] = useUpsertHeatProfiles();

  const onSaveHeatProfile = React.useCallback(async () => {
    const hasNameError = !isHeatProfileNameValid(state.name);

    if (hasNameError) return;

    const created = new Date().toISOString();

    const mapped = {
      ...(profile ?? {}),
      ...state,
      id: profile?.id ?? ulid(),
      units: appSettings.tempPreference,
      version: ProfileVersion.T,
      order: profile?.order ?? 0,
      created: profile?.created ?? created,
      modified: profile?.modified ?? created,
    };

    upsertAccountHeatProfiles([mapped]);

    onSave();

    navigation.goBack();
  }, [
    appSettings.tempPreference,
    state,
    profile,
    onSave,
    upsertAccountHeatProfiles,
  ]);

  React.useEffect(() => {
    navigation.setOptions({
      headerLeft: ({onPress}: HeaderBackButtonProps) => (
        <BackButton {...{onPress}} color={titleStyle?.color} />
      ),
      headerRight: () => (
        <Button variant="transparent" onPress={onSaveHeatProfile}>
          <CheckIcon size={28} color={titleStyle?.color} />
        </Button>
      ),
      headerTitle: () => <HeaderTitle style={titleStyle}>{''}</HeaderTitle>,
      headerShown: true,
    });
  }, [titleStyle, navigation, onSaveHeatProfile]);

  const onDisabledPress = React.useCallback(() => {
    accessMoodLight({profileId: profile?.id});
  }, [accessMoodLight, profile]);

  useSpinner({isVisible: loading});

  return (
    <MainContentContainer>
      <ScrollableContainer
        contentContainerStyle={{
          height: Constants.IS_WEB ? 0 : undefined,
        }}>
        <ContentContainer
          style={{
            width: '100%',
            alignItems: 'center',
          }}>
          <HeaderContainer
            style={{marginTop: Constants.IS_WEB ? top + 20 : 10}}>
            <HeatProfileNameTextInput
              value={state.name}
              onChangeText={text => {
                setState(previous => ({
                  ...previous,
                  name: text?.toUpperCase() ?? '',
                }));
              }}
              style={{
                width: inputWidth,
              }}
            />
          </HeaderContainer>

          <MainSliderContainer>
            <TemperatureSlider
              value={state.temperature}
              min={minTemp}
              max={maxTemp}
              style={{paddingBottom: isSmall ? 24 : 40}}
              onEnd={temperature => {
                setState(previous => ({...previous, temperature}));
              }}
            />

            <DurationSlider
              value={state.duration}
              max={Constants.DURATION_MAX}
              min={Constants.DURATION_MIN}
              style={{paddingBottom: isSmall ? 24 : 40}}
              onEnd={duration => {
                setState(previous => ({...previous, duration}));
              }}
            />

            <VaporControlSlider
              value={state.vaporSetting}
              onEnd={vaporSetting => {
                setState(previous => ({...previous, vaporSetting}));
              }}
              disabled={!isVaporEnabled}
              {...{vapors}}
            />

            <VaporControlUpsellText
              {...{isMeetAC, is3dChamber, isVaporEnabled}}
              style={{
                marginTop: isSmall ? 5 : 10,
                marginBottom: isSmall ? 8 : 16,
              }}
            />

            <TextSwitch
              initialHandlePosition={initialSwitchValue}
              leftText={Strings.COLOR}
              rightText={Strings.MOOD}
              disabledPosition={
                !canToggleMoodLights
                  ? TextSwitchHandlePosition.RIGHT
                  : undefined
              }
              onDisabledPress={onDisabledPress}
              onToggle={setTextSwitchPosition}
            />

            <TextSwitchBottomContainer>
              {textSwitchPosition === TextSwitchHandlePosition.LEFT ? (
                <ColorSliderContainer>
                  <ColorSlider
                    value={displayColors}
                    onChange={v => throttledSetColor(convertHexToHsl(v[0]))}
                    onEnd={v => throttledSetColor(convertHexToHsl(v[0]))}
                  />
                </ColorSliderContainer>
              ) : (
                <MoodLightButtonContainer
                  onPress={() => {
                    if (!canToggleMoodLights) {
                      onDisabledPress();
                    } else {
                      navigation.navigate(
                        ...toSelectMoodLight(
                          isMoodLightId(state.light)
                            ? {
                                selectedId: state.light.id,
                              }
                            : undefined,
                        ).encode(),
                      );
                    }
                  }}>
                  <MoodLightButtonText>
                    {Messages.HEAT_PROFILE_EDIT_MOOD_LIGHT_BTN}
                  </MoodLightButtonText>

                  <MoodLightButtonRight>
                    {moodLight && (
                      <>
                        <GradientCircle style={{marginRight: 10}}>
                          <MoodLightCircle size={16} {...{theme, moodLight}} />
                        </GradientCircle>

                        <MoodLightButtonRightText numberOfLines={1}>
                          {moodLight.name}
                        </MoodLightButtonRightText>
                      </>
                    )}

                    <ChevronRightIcon color={colors.white} size={32} />
                  </MoodLightButtonRight>
                </MoodLightButtonContainer>
              )}
            </TextSwitchBottomContainer>
          </MainSliderContainer>
        </ContentContainer>
      </ScrollableContainer>
    </MainContentContainer>
  );
};

// Header
const HeaderContainer = styled(View)({
  maxWidth: '80%',
  height: 200,
  flexDirection: 'column',
  alignItems: 'center',
});

const MainContentContainer = styled(View)({
  width: '100%',
  flex: 1,
  backgroundColor: colors.black,
});

const ScrollableContainer = styled(ScrollView)({
  flex: 1,
});

const ContentContainer = styled(AutoAdjustScreen)({
  alignItems: 'center',
  marginBottom: 40,
});

// Footer
const MainSliderContainer = styled(View)({
  display: 'flex',
  flexDirection: 'column',
  width: '90%',
});

const TextSwitchBottomContainer = styled(View)({
  height: 40, // Matches slider height
  marginTop: 24,
  marginBottom: 40,
});

const ColorSliderContainer = styled(View)({
  marginTop: 10,
});

const MoodLightButtonText = styled(Text)({
  fontFamily: 'Roboto-Regular',
  fontWeight: '400',
  fontSize: 16,
  color: colors.white,
});

const MoodLightButtonContainer = styled(Pressable)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 12,
  backgroundColor: colors.gray19,
  width: '100%',
  height: 56,
  paddingRight: 5,
  paddingLeft: 24,
});

const MoodLightButtonRight = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
});

const MoodLightButtonRightText = styled(Text)({
  fontFamily: 'Roboto-Regular',
  fontWeight: '400',
  color: colors.white50,
  fontSize: 16,
});

const GradientCircle = styled(View)({
  height: 16,
  width: 16,
  borderRadius: 1000,
  backgroundColor: 'transparent',
  alignItems: 'center',
  justifyContent: 'center',
});

const HeaderTitle = styled(AppText)({
  fontFamily: 'Roboto-Bold',
  fontSize: 14,
  marginTop: 10,
});
